/**
 * Case-type landing page content.
 *
 * Pure data — no React and no icon imports, so this module stays unit-testable
 * and can derive its own PAGE_META entries.
 *
 * Each page must carry substance specific to its claim type. Near-identical
 * pages that differ only by a swapped keyword are doorway pages, and that
 * penalty can reach the whole domain — so `whatYouNeed` and `faqs` are the
 * sections that must never be templated. The tests in caseTypePages.test.js
 * only catch literal string duplication and near-empty placeholder text; they
 * cannot mechanically detect a page templated down to a single swapped keyword
 * while still producing distinct, adequately long strings. Substantive
 * distinctiveness across pages is a review responsibility, not something these
 * tests enforce.
 *
 * Copy rules enforced by caseTypePages.test.js:
 *   - no percentage, dollar figure, or duration anywhere
 *   - never the word "loan"
 * Amounts and pricing live on the FAQ; these pages link there.
 *
 * See docs/superpowers/specs/2026-07-31-case-type-pages-design.md.
 */

export const CASE_TYPE_DISCLAIMER =
  'This page is general information about how claims of this type usually work. It is not legal advice and it is not a description of your case. Your attorney is the right person to tell you how any of it applies to you.';

export const CASE_TYPE_PAGES = [
  {
    slug: 'auto-accident',
    path: '/auto-accident-funding',
    caseTypeValue: 'auto_accident',
    badge: 'Auto Accident Funding',
    h1: 'Pre-Settlement Funding for Auto Accident Claims',
    intro: [
      'A car accident can stop your income and start your bills on the same day. If an attorney is representing you and your claim is still open, you may be able to access part of your expected settlement now instead of waiting for the case to finish.',
      'MyCaseLoan is a marketplace, not a lender. Your attorney verifies the claim, qualified funders review it, and you compare whatever offers they make — or accept none of them.',
    ],
    whatYouNeed: {
      heading: 'What an auto accident claim needs',
      items: [
        {
          title: 'A police report',
          description:
            'For motor vehicle accidents a copy of the police report is required. You can submit your application before you have it and add it once you do, though providing it sooner helps funders review your case.',
        },
        {
          title: 'The insurance picture',
          description:
            'Car crashes frequently involve more than one policy — the other driver’s liability coverage, your own policy, and sometimes uninsured or underinsured motorist coverage. Which coverage is actually available shapes what a settlement can realistically pay, so funders pay close attention to it.',
        },
        {
          title: 'Your medical treatment record',
          description:
            'What treatment you have had, and whether it is finished or ongoing. This is normally gathered through your attorney rather than from you directly, so there is nothing for you to compile before applying.',
        },
        {
          title: 'Your attorney’s details',
          description:
            'Your attorney has to verify the claim before any funder can evaluate it. If they are not on the platform yet, enter their information and we will invite them — there is no cost to them.',
        },
      ],
    },
    howItWorks: [
      {
        title: 'Apply with your accident details',
        description:
          'Your case type, the state where the crash happened, your attorney, and how much funding you need. It takes a few minutes.',
      },
      {
        title: 'Your attorney verifies the claim',
        description:
          'They confirm they represent you and add the detail funders need — how the crash happened, the treatment involved, and the coverage in play.',
      },
      {
        title: 'Funders review and make offers',
        description:
          'You compare the cash amount, fees, rate and cap of each offer side by side, and choose the one that suits you.',
      },
    ],
    faqs: [
      {
        q: 'Do I need the police report before I apply?',
        body: [
          'No. Apply first and add it when you have it. It is required for motor vehicle claims, but it does not have to be in hand to start, and your attorney can usually obtain a copy.',
        ],
      },
      {
        q: 'Is the damage to my car part of this?',
        body: [
          'Generally no. The claim to repair or replace your vehicle is usually handled separately from the claim for your injuries. Pre-settlement funding relates to the injury claim.',
        ],
      },
      {
        q: 'What if the other driver had no insurance?',
        body: [
          'A claim may still exist, often through uninsured or underinsured motorist coverage on your own policy. What matters for funding is what coverage your attorney identifies as available, because that is what a settlement would be paid from.',
        ],
      },
      {
        q: 'Does an advance affect my own insurance?',
        body: [
          'No. The advance is an arrangement between you and the funder, repaid from your settlement by your attorney when the case resolves. It is not a claim against your policy and does not involve your insurer.',
        ],
      },
      {
        q: 'What if I was partly responsible for the crash?',
        body: [
          'States handle shared fault differently, and it can affect what a claim is worth rather than ending it. Your attorney can explain how your state treats it; funders weigh whatever your attorney verifies.',
        ],
      },
    ],
    meta: {
      title: 'Auto Accident Pre-Settlement Funding | MyCaseLoan',
      description:
        'Compare pre-settlement funding offers on a car accident claim. No credit check, nothing upfront, and you repay only if your case succeeds.',
    },
  },

  {
    slug: 'rideshare-accident',
    path: '/rideshare-accident-funding',
    caseTypeValue: 'rideshare_accident',
    badge: 'Ride Share Accident Funding',
    h1: 'Pre-Settlement Funding for Ride Share Accident Claims',
    intro: [
      'Crashes involving a ride share vehicle carry an insurance picture that ordinary car accidents do not. Whether you were the passenger, another driver, a cyclist, or a pedestrian, sorting out which policy responds can take time — which is exactly when bills become a problem.',
      'If an attorney represents you and the claim is open, you may be able to access part of your expected settlement now. MyCaseLoan is a marketplace: your attorney verifies the claim and funders compete for it.',
    ],
    whatYouNeed: {
      heading: 'What a ride share claim needs',
      items: [
        {
          title: 'The driver’s app status when the crash happened',
          description:
            'This is usually the single most important detail. Ride share companies carry commercial coverage, but which policy responds generally depends on what the driver was doing at that moment — app off, app on and waiting for a request, on the way to collect someone, or carrying a passenger. It is a common reason these claims take longer to resolve than an ordinary crash.',
        },
        {
          title: 'Your trip record',
          description:
            'The trip receipt, the driver’s name, and the ride history in your app establish that this was a ride share trip. Save or screenshot it early — access to that history is not always permanent, and it is far easier to keep than to recover later.',
        },
        {
          title: 'A police report',
          description:
            'As with any motor vehicle accident, a copy of the police report is required. You can apply before you have it and provide it afterwards.',
        },
        {
          title: 'Your attorney’s details',
          description:
            'Your attorney verifies the claim before funders see it. Ride share claims often involve more than one insurer, and sorting out who is responsible is work that sits with your attorney, not with you.',
        },
      ],
    },
    howItWorks: [
      {
        title: 'Apply with your trip and crash details',
        description:
          'Select ride share as your case type, tell us the state, your attorney, and the funding you need.',
      },
      {
        title: 'Your attorney verifies the claim',
        description:
          'They confirm representation and add what funders need, including which insurer they expect to respond.',
      },
      {
        title: 'Funders review and make offers',
        description:
          'You compare offers side by side and choose one, or none. Nothing obliges you to accept.',
      },
    ],
    faqs: [
      {
        q: 'Does it matter whether I was the passenger or in another car?',
        body: [
          'It changes whose insurance is involved, not whether you can apply. Passengers, other drivers, cyclists, and pedestrians can all have claims arising from a ride share crash.',
        ],
      },
      {
        q: 'The driver was logged in but had no passenger. Does that change things?',
        body: [
          'It can. Coverage available in a ride share crash commonly differs depending on whether the driver was waiting for a request, traveling to a pickup, or carrying a rider. Your attorney will establish which applied.',
        ],
      },
      {
        q: 'Why do ride share claims seem to take longer?',
        body: [
          'There are usually more parties: the driver, the company’s insurer, and often another vehicle’s insurer. Establishing which policy responds adds a step that an ordinary two-car crash does not have.',
        ],
      },
      {
        q: 'I did not save the trip receipt. Is that a problem?',
        body: [
          'It is not fatal. Your attorney can usually obtain trip records through the ride share company, though having it yourself makes the early stages easier.',
        ],
      },
      {
        q: 'Can I apply if the ride share driver was not at fault?',
        body: [
          'Yes. What matters is that you have an injury claim an attorney is pursuing, not which driver was responsible.',
        ],
      },
    ],
    meta: {
      title: 'Ride Share Accident Funding | MyCaseLoan',
      description:
        'Pre-settlement funding for Uber and Lyft accident claims. Compare offers from multiple funders, with no credit check and nothing to pay upfront.',
    },
  },

  {
    slug: 'slip-and-fall',
    path: '/slip-and-fall-funding',
    caseTypeValue: 'slip_fall',
    badge: 'Slip and Fall Funding',
    h1: 'Pre-Settlement Funding for Slip and Fall Claims',
    intro: [
      'A fall on someone else’s property can leave you injured and out of work while the property owner’s insurer takes its time. If an attorney is handling your claim, you may be able to access part of your expected settlement before the case resolves.',
      'MyCaseLoan connects your verified claim to funders who compete for it. We are a marketplace, not a lender.',
    ],
    whatYouNeed: {
      heading: 'What a slip and fall claim needs',
      items: [
        {
          title: 'Evidence of the hazard, as early as possible',
          description:
            'This is what separates strong premises claims from weak ones. The spill gets mopped, the broken step gets repaired, the ice melts. Photographs taken at the time — of the hazard itself, the lighting, and whether any warning sign was present — are frequently the most valuable material in the entire file.',
        },
        {
          title: 'The incident report',
          description:
            'Most businesses complete an internal report when someone is hurt on their premises. Ask for a copy at the time if you are able to; afterwards, your attorney can request it as part of the claim.',
        },
        {
          title: 'Witnesses',
          description:
            'Names and contact details for anyone who saw you fall, or who noticed the hazard beforehand. A witness who saw the hazard before the fall can matter more than one who only saw the fall itself.',
        },
        {
          title: 'Where it happened, and who controls it',
          description:
            'A shop, a restaurant, a car park, and an apartment building can each involve different responsible parties — an occupier, an owner, or a maintenance contractor. Establishing who was responsible for the area is part of what your attorney works out.',
        },
      ],
    },
    howItWorks: [
      {
        title: 'Apply with the details of your fall',
        description:
          'Case type, the state, your attorney, and the funding you need. No documents are needed to start.',
      },
      {
        title: 'Your attorney verifies the claim',
        description:
          'They confirm representation and add the case detail funders need, including what is known about the hazard and who is responsible.',
      },
      {
        title: 'Funders review and make offers',
        description:
          'Compare the offers you receive on amount, fees, rate and cap, then choose whichever suits you best.',
      },
    ],
    faqs: [
      {
        q: 'I did not report it to the business that day. Does that end my claim?',
        body: [
          'Not on its own. Plenty of people leave and only realize later how badly they are hurt. It may become something the other side raises, and your attorney can advise how much weight it carries where you are.',
        ],
      },
      {
        q: 'I have no photographs of what I slipped on. Can I still apply?',
        body: [
          'Yes. Photographs help a claim, but they are not a requirement for applying. Incident reports, witnesses, maintenance records, and CCTV can all establish what happened.',
        ],
      },
      {
        q: 'The business says I was not looking where I was going.',
        body: [
          'Shared responsibility is common in premises claims, and states treat it differently — in some it reduces what a claim is worth rather than defeating it. Your attorney can explain how your state approaches it.',
        ],
      },
      {
        q: 'Does it matter that I fell in an apartment building where I live?',
        body: [
          'It affects who is responsible rather than whether a claim exists. Responsibility for a common area typically sits with whoever controls and maintains it.',
        ],
      },
      {
        q: 'Can I get funding before liability is decided?',
        body: [
          'Funders evaluate what your attorney has verified, and most claims are still being contested at that stage. What they are weighing is the claim as it stands, not a finished case.',
        ],
      },
    ],
    meta: {
      title: 'Slip and Fall Pre-Settlement Funding | MyCaseLoan',
      description:
        'Pre-settlement funding for slip and fall and premises liability claims. Attorney-verified, no credit check, and repaid only if your case succeeds.',
    },
  },

  {
    slug: 'dog-bite',
    path: '/dog-bite-funding',
    caseTypeValue: 'dog_bite',
    badge: 'Dog Bite Funding',
    h1: 'Pre-Settlement Funding for Dog Bite Claims',
    intro: [
      'Dog bite injuries often mean immediate treatment, a real risk of infection, and sometimes scarring that needs further care. If an attorney is pursuing a claim for you, funding can cover what you need while that claim runs.',
      'Your attorney verifies the claim, funders review it and make offers, and you compare them. Nothing is repaid unless your case succeeds.',
    ],
    whatYouNeed: {
      heading: 'What a dog bite claim needs',
      items: [
        {
          title: 'The animal control or health department report',
          description:
            'Bites are usually reportable to animal control or a local health authority. That report identifies the animal and its owner, records the injury, and often captures the animal’s history — which makes it the backbone of most dog bite claims.',
        },
        {
          title: 'Photographs of the injury over time',
          description:
            'Bite wounds change as they heal, and scarring is frequently part of what a claim covers. Pictures taken at intervals during recovery show something that a medical note alone does not, and they matter here more than in most injury claims.',
        },
        {
          title: 'Who owned or was handling the dog',
          description:
            'The owner’s identity matters, and so does whose property you were on when it happened. In many claims it is the owner’s homeowner’s or renter’s insurance that ultimately responds, rather than the owner paying out of pocket.',
        },
        {
          title: 'Your attorney’s details',
          description:
            'State law differs sharply on dog bites — some states hold the owner responsible regardless of the animal’s history, while others weigh what the owner knew about the dog. Your attorney establishes which framework applies to you.',
        },
      ],
    },
    howItWorks: [
      {
        title: 'Apply with the details of the bite',
        description:
          'Case type, the state where it happened, your attorney, and the funding you need.',
      },
      {
        title: 'Your attorney verifies the claim',
        description:
          'They confirm representation and add what funders need, including the report and who is expected to be responsible.',
      },
      {
        title: 'Funders review and make offers',
        description:
          'You compare whatever offers arrive and choose one, or decline them all.',
      },
    ],
    faqs: [
      {
        q: 'The owner is a friend. Am I suing them personally?',
        body: [
          'Usually the practical answer is that their insurance responds rather than the person. Many homeowner and renter policies cover this kind of claim, which is why bringing one does not always mean what people expect. Your attorney can explain how it would work in your situation.',
        ],
      },
      {
        q: 'The dog had never bitten anyone before. Does that end the claim?',
        body: [
          'It depends where you are. Some states hold owners responsible regardless of the animal’s past, while others give weight to whether the owner knew the dog might be dangerous. Which rule applies is a question for your attorney.',
        ],
      },
      {
        q: 'I was bitten at the owner’s home. Does that matter?',
        body: [
          'Where it happened and why you were there can both matter, particularly whether you were invited. It is a common thing for the other side to raise, and it is worth telling your attorney early.',
        ],
      },
      {
        q: 'The dog knocked me over but did not bite me.',
        body: [
          'Injuries caused by a dog without a bite can still support a claim. Whether the same rules apply as they would to a bite varies, so it is worth raising specifically with your attorney.',
        ],
      },
      {
        q: 'Do I need the animal control report to apply?',
        body: [
          'No. Apply without it — your attorney will normally obtain it as part of building the claim.',
        ],
      },
    ],
    meta: {
      title: 'Dog Bite Pre-Settlement Funding | MyCaseLoan',
      description:
        'Pre-settlement funding for dog bite injury claims. Attorney-verified, no credit check, nothing upfront, repaid only from a successful settlement.',
    },
  },

  {
    slug: 'medical-malpractice',
    path: '/medical-malpractice-funding',
    caseTypeValue: 'medical_malpractice',
    badge: 'Medical Malpractice Funding',
    h1: 'Pre-Settlement Funding for Medical Malpractice Claims',
    intro: [
      'Medical malpractice claims are among the slowest injury cases to resolve, and they are often brought by people already dealing with the consequences of the harm itself. Funding can bridge that gap while your attorney does the work.',
      'MyCaseLoan is a marketplace. Your attorney verifies the claim, qualified funders evaluate it, and you compare their offers.',
    ],
    whatYouNeed: {
      heading: 'What a medical malpractice claim needs',
      items: [
        {
          title: 'An expert review of the care you received',
          description:
            'Many states require a claim to be supported by a qualified medical expert before it can proceed, often called a certificate or affidavit of merit. What is required differs by state and by the kind of claim. This step happens early, and it is a common reason these cases move more slowly than other injury claims.',
        },
        {
          title: 'Your complete medical records',
          description:
            'These claims are built almost entirely from records — charts, imaging, medication logs, and notes establishing who did what and when. The volume is usually far greater than in other injury claims, and assembling it is work your attorney handles.',
        },
        {
          title: 'The treatment timeline',
          description:
            'When you were first seen, what was done, when the problem emerged, and what treatment followed. Malpractice claims often turn on sequence, so the order of events carries weight that it would not in a car accident claim.',
        },
        {
          title: 'Your attorney’s details',
          description:
            'Filing deadlines for malpractice claims frequently work differently from other injury claims, and in some states the clock can start when the harm was discovered rather than when it occurred. Your attorney will know what governs your claim.',
        },
      ],
    },
    howItWorks: [
      {
        title: 'Apply with the details of your claim',
        description:
          'Case type, the state, your attorney, and the funding you need. No records are required to apply.',
      },
      {
        title: 'Your attorney verifies the claim',
        description:
          'They confirm representation and add what funders need, including where the claim stands in the process.',
      },
      {
        title: 'Funders review and make offers',
        description:
          'You compare offers on amount, fees, rate and cap, and choose whichever works for you.',
      },
    ],
    faqs: [
      {
        q: 'Does a bad outcome mean there was malpractice?',
        body: [
          'No, and this is the most common misunderstanding about these claims. Medicine carries risk, and a poor result is not by itself evidence that care fell below an accepted standard. Establishing that gap is what the expert review exists to assess.',
        ],
      },
      {
        q: 'Why do these claims take so much longer?',
        body: [
          'The expert review comes first, the records are voluminous, and malpractice claims are usually defended more heavily than other injury claims. Each of those adds time before the case reaches a resolution.',
        ],
      },
      {
        q: 'Do I need the expert report before I apply?',
        body: [
          'No. Apply whenever your attorney is engaged. Where the claim has reached in that process is something funders will see through your attorney’s verification.',
        ],
      },
      {
        q: 'I am still being treated by the same provider. Is that a problem?',
        body: [
          'It is a situation your attorney will want to know about early, because it can affect both your care and the claim. It does not prevent you from applying.',
        ],
      },
      {
        q: 'The harm happened a while ago. Is it too late?',
        body: [
          'Not necessarily. Deadlines for malpractice claims work differently from other injury claims, and in some states they run from when the harm was discovered. Only your attorney can tell you where you stand.',
        ],
      },
    ],
    meta: {
      title: 'Medical Malpractice Funding | MyCaseLoan',
      description:
        'Pre-settlement funding for medical malpractice claims. Attorney-verified, no credit check, and repaid only if your case resolves in your favor.',
    },
  },
];

/** Resolve a slug to its page, or null when unknown. */
export function findCaseTypePage(slug) {
  return CASE_TYPE_PAGES.find((page) => page.slug === slug) || null;
}
